import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit,
  Delete,
  DevicesOther,
  CheckCircle,
  Warning,
  HelpOutline,
} from '@mui/icons-material';
import {
  MonitorStatusChip,
  formatMonitorDate,
  formatMonitorDateTime,
} from '../../../utils/monitorDisplayUtils';
import { UseDispositivosRedeReturn } from './useDispositivosRede';
import {
  STATUS_COLORS,
  StatCard,
  getTipoLabel,
  getIpExibicao,
  getMonitorLatenciaExibicao,
} from './monitoramentoRedeUtils';
import DispositivoFormDialog from './DispositivoFormDialog';
import DispositivoDeleteDialog from './DispositivoDeleteDialog';

interface DispositivosTabProps {
  dispositivosState: UseDispositivosRedeReturn;
}

const DispositivosTab: React.FC<DispositivosTabProps> = ({ dispositivosState }) => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const {
    dispositivos,
    loading,
    stats,
    filteredDispositivos,
    activeStatusFilter,
    handleFilterChange,
    handleOpenEdit,
    setDeleteTarget,
  } = dispositivosState;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <StatCard
          label="Total"
          value={stats.total}
          icon={<DevicesOther />}
          active={activeStatusFilter === 'todos'}
          onClick={() => handleFilterChange('todos')}
        />
        <StatCard
          label="Online"
          value={stats.online}
          color={STATUS_COLORS.online}
          icon={<CheckCircle />}
          active={activeStatusFilter === 'online'}
          onClick={() => handleFilterChange('online')}
        />
        <StatCard
          label="Offline"
          value={stats.offline}
          color={STATUS_COLORS.offline}
          icon={<Warning />}
          active={activeStatusFilter === 'offline'}
          onClick={() => handleFilterChange('offline')}
        />
        <StatCard
          label="Desconhecido"
          value={stats.unknown}
          color={STATUS_COLORS.unknown}
          icon={<HelpOutline />}
          active={activeStatusFilter === 'unknown'}
          onClick={() => handleFilterChange('unknown')}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minHeight: 520,
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
            <CircularProgress size={48} thickness={4} />
            <Typography color="text.secondary" fontWeight={500}>Carregando dispositivos...</Typography>
          </Box>
        ) : filteredDispositivos.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
            <Alert severity="info" sx={{ maxWidth: 480 }}>
              {dispositivos.length === 0
                ? 'Nenhum dispositivo cadastrado para monitoramento.'
                : 'Nenhum dispositivo encontrado com os filtros aplicados.'}
            </Alert>
          </Box>
        ) : (
          <TableContainer sx={{ height: '100%' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                      fontWeight: 700,
                      fontSize: '0.73rem',
                      py: 0.8,
                      borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>IP / Destino</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Latência</TableCell>
                  <TableCell>Monitorar até</TableCell>
                  <TableCell>Último online</TableCell>
                  <TableCell>Último offline</TableCell>
                  <TableCell>Último ping</TableCell>
                  <TableCell align="center">Ativo</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDispositivos.map((dispositivo) => (
                  <TableRow key={dispositivo.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{dispositivo.nome}</Typography>
                      {dispositivo.tipo === 'cliente' && dispositivo.contrato_servico?.contrato?.cliente && (
                        <Typography variant="caption" color="text.secondary">
                          {dispositivo.contrato_servico.contrato.cliente.nome}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={getTipoLabel(dispositivo.tipo)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                        {dispositivo.tipo === 'cliente' && dispositivo.modo_monitoramento === 'status_conexao'
                          ? 'Status Conexão (RADIUS)'
                          : getIpExibicao(dispositivo)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <MonitorStatusChip status={dispositivo.monitor_status} />
                    </TableCell>
                    <TableCell>
                      {getMonitorLatenciaExibicao(dispositivo.tipo, dispositivo.monitor_latencia_ms) ?? '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {formatMonitorDate(dispositivo.monitorar_ate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {formatMonitorDateTime(dispositivo.monitor_ultimo_online_em)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {formatMonitorDateTime(dispositivo.monitor_ultimo_offline_em)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {formatMonitorDateTime(dispositivo.monitor_ultimo_ping_em)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={dispositivo.ativo ? 'Sim' : 'Não'}
                        color={dispositivo.ativo ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenEdit(dispositivo)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(dispositivo)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <DispositivoFormDialog {...dispositivosState} />
      <DispositivoDeleteDialog {...dispositivosState} />
    </>
  );
};

export default DispositivosTab;
